// AI 이미지 개선 관련 함수들

// API Base URL 결정 함수
const getApiBaseUrl = () => {
  // 프로덕션 환경에서는 현재 도메인 사용
  if (process.env.NODE_ENV === 'production') {
    return window.location.origin;
  }
  
  // 개발 환경에서는 React 개발 서버가 3000 포트, 백엔드가 3000 포트 (통합)
  // 또는 환경변수로 백엔드 URL을 지정할 수 있음
  return process.env.REACT_APP_API_BASE_URL || window.location.origin;
};

// AI 이미지 개선 메인 함수
export const enhanceImageWithAI = async (imageFile, onProgress = null) => {
  try {
    // 진행률 업데이트 함수
    const updateProgress = (percent, message) => {
      if (onProgress) {
        onProgress({ percent, message });
      }
    };
    
    // 시작
    updateProgress(0, '이미지 준비 중...');
    
    // 이미지 파일을 base64로 변환
    const imageBase64 = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(imageFile);
    });
    
    updateProgress(5, '서버에 전송 중...');
    
    const apiBaseUrl = getApiBaseUrl();
    
    // 진행률 시뮬레이션을 위한 인터벌
    let progressValue = 10;
    const progressInterval = setInterval(() => {
      if (progressValue < 90) {
        progressValue += Math.random() * 5 + 2; // 2-7% 랜덤 증가
        progressValue = Math.min(progressValue, 90);
        
        let message = 'AI 처리 중...';
        if (progressValue < 30) {
          message = 'GFPGAN으로 얼굴 복원 중...';
        } else if (progressValue < 60) {
          message = 'GFPGAN 처리 중...';
        } else if (progressValue < 85) {
          message = '배경 제거 처리 중...';
        } else {
          message = '최종 처리 중...';
        }
        
        updateProgress(Math.floor(progressValue), message);
      }
    }, 500);
    
    // 통합된 백엔드를 통해 AI 이미지 개선 요청
    const response = await fetch(`${apiBaseUrl}/api/enhance-image`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        imageFile: imageBase64
      })
    });
    
    clearInterval(progressInterval);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(errorData.error || `HTTP ${response.status}`);
    }

    updateProgress(95, '결과 처리 중...');
    const result = await response.json();
    
    if (!result.success) {
      throw new Error(result.error || 'AI 이미지 개선 실패');
    }

    // 모든 단계의 이미지를 File 객체로 변환
    const convertToFile = async (url, filename) => {
      if (!url) {
        return null;
      }
      try {
        const response = await fetch(url);
        const blob = await response.blob();
        return new File([blob], filename, { type: 'image/jpeg' });
      } catch (error) {
        return null;
      }
    };
    
    // 다양한 필드명 시도
    const getImageUrl = (result, ...possibleKeys) => {
      for (const key of possibleKeys) {
        if (result[key]) return result[key];
      }
      return null;
    };
    
    const gfpganUrl = getImageUrl(result, 'gfpgan_headshot', 'gfpgan', 'gfpgan_image');
    const gfpganBackgroundRemoverUrl = getImageUrl(result, 'gfpgan_background_remover_headshot', 'gfpgan_background_remover');
    const finalUrl = getImageUrl(result, 'final_headshot', 'final', 'enhanced', 'processed', 'result');
    
    const gfpganFile = gfpganUrl ? await convertToFile(gfpganUrl, `gfpgan-${Date.now()}.jpg`) : null;
    const gfpganBackgroundRemoverFile = gfpganBackgroundRemoverUrl ? await convertToFile(gfpganBackgroundRemoverUrl, `gfpgan-bg-remover-${Date.now()}.jpg`) : null;
    const finalFile = finalUrl ? await convertToFile(finalUrl, `final-${Date.now()}.jpg`) : null;
    
    updateProgress(100, '이미지 개선 완료!');

    return {
      originalFile: imageFile,
      gfpganFile,
      gfpganBackgroundRemoverFile,
      finalFile,
      // URL들도 함께 반환
      gfpganUrl,
      gfpganBackgroundRemoverUrl,
      finalUrl,
      // 기본 선택 타입 설정
      selectedImageType: finalUrl ? 'final' : (gfpganBackgroundRemoverUrl ? 'gfpgan-bg-remover' : (gfpganUrl ? 'gfpgan' : 'original'))
    };

  } catch (error) {
    throw error;
  }
};
